/**
 * What a mark asks a player to do, worked out as notes and times.
 *
 * The document holds signs, not performances: a trill is a wiggle drawn over a note, and
 * `document/types.ts` is careful to say that it does not stand for any particular run of
 * notes. That is right for a document -- what a trill asks for depends on the century, the
 * instrument and the edition -- and useless for playback, where something has to be heard.
 *
 * This file is where the guess is made, once, in one place, and kept out of the document:
 * nothing here changes a score, and a caller that wants the written notes and nothing else
 * turns the whole of it off with one option. The readings are the ordinary modern ones --
 * a trill starts on the note it is written over, a mordent bites the step below it, a turn
 * goes above, through, below and back -- and a player who wants the other convention writes
 * the notes out, which is what a player who cares does anyway.
 *
 * Everything is counted in ticks from the start of the note the mark is on, and in
 * semitones away from the pitch it is written at. Neither depends on where the note is in
 * the piece, on how fast the music is going, or on what the part transposes by, so a
 * sounding note is the written one moved by both numbers and nothing else has to be known
 * here.
 */

import { PPQ } from "../document/duration";
import { STEPS, diatonicOf, keyAlter, midiOf, pitchOf } from "../document/pitch";
import type { NotePitch, Step } from "../document/pitch";
import type { Articulation, FermataShape, NoteEvent } from "../document/types";

/** One sounding of a written note: how far from it, how long after it, and for how long. */
export interface Stroke {
  /** Semitones away from the written pitch. `0` is the note itself. */
  readonly semitones: number;
  /** Ticks from the start of the written note. */
  readonly start: number;
  readonly ticks: number;
}

/**
 * The shortest an ornament's note is allowed to be: a semiquaver.
 *
 * Ornaments are quick but not arbitrarily quick -- a mordent on a minim is two fast notes
 * and then the note, not three notes of a minim each. Where the written note is too short
 * to hold notes of this length the ornament divides it instead, which is what makes a turn
 * on a quaver four equal semiquavers rather than something that runs past the beat.
 */
const QUICK = PPQ / 4;

/** How much longer than written a fermata holds, by the shape it is drawn with. */
const HOLDS: Readonly<Record<FermataShape, number>> = {
  angled: 1.5,
  normal: 2,
  square: 3,
};

/** How an articulation changes the length and the weight of the note it is written on. */
export interface Shape {
  /** A multiplier on the written length: below 1 detaches the note from the next. */
  readonly length: number;
  /** A multiplier on the dynamic in force. */
  readonly velocity: number;
}

const SHAPES: Readonly<Record<Articulation, Shape>> = {
  staccato: { length: 0.5, velocity: 1 },
  staccatissimo: { length: 0.3, velocity: 1.05 },
  accent: { length: 1, velocity: 1.3 },
  "strong-accent": { length: 0.8, velocity: 1.45 },
  tenuto: { length: 1, velocity: 1 },
  "detached-legato": { length: 0.75, velocity: 1 },
  // A breath is taken out of the note it is marked after and never added to the bar, so the
  // other players are not made to wait for it: the note loses its last quarter, the least
  // any mark here shortens a note by.
  "breath-mark": { length: 0.75, velocity: 1 },
  // A natural harmonic is written at the pitch it sounds, so the note is played as it is.
  harmonic: { length: 1, velocity: 1 },
};

/** Both multipliers of everything written on one note, which stack. */
export function shapeOf(articulations: readonly Articulation[]): Shape {
  let length = 1;
  let velocity = 1;
  for (const mark of articulations) {
    const shape = SHAPES[mark];
    length *= shape.length;
    velocity *= shape.velocity;
  }
  return { length, velocity };
}

/** How much longer than its written length a held note or rest lasts. */
export function heldFor(shape: FermataShape, ticks: number): number {
  return Math.round(ticks * (HOLDS[shape] - 1));
}

/**
 * A scale step away from a pitch, in semitones.
 *
 * The step is diatonic -- a letter up or down, with whatever the key signature does to that
 * letter -- because that is what an ornament sign means: a mordent in D major bites the C
 * sharp, and nobody writes the accidental. The note's own accidental is deliberately not
 * read: a raised leading note in a minor key is still ornamented from the scale, and a
 * player who wants otherwise writes the notes.
 */
function degree(pitch: NotePitch, fifths: number, steps: number): number {
  const ladder = diatonicOf(pitch) + steps;
  const octave = Math.floor(ladder / 7);
  const step = STEPS[((ladder % 7) + 7) % 7] as Step;
  return midiOf(pitchOf(step, keyAlter(fifths, step), octave)) - midiOf(pitch);
}

/** A run of notes of equal length, filling `ticks` exactly however it divides. */
function evenly(semitones: readonly number[], count: number, ticks: number): Stroke[] {
  const strokes: Stroke[] = [];
  for (let at = 0; at < count; at += 1) {
    const start = Math.round((at * ticks) / count);
    const next = Math.round(((at + 1) * ticks) / count);
    strokes.push({ semitones: semitones[at % semitones.length] ?? 0, start, ticks: next - start });
  }
  return strokes;
}

/**
 * Quick notes and then the rest of the note: a mordent, a turn, a slide.
 *
 * The last of the written semitones takes whatever time the quick ones leave, which is what
 * makes one shape serve both a mordent on a minim, where the bite is fast and the note is
 * long, and a mordent on a semiquaver, where the three notes are equal.
 */
function bitten(semitones: readonly number[], ticks: number): Stroke[] {
  const quick = Math.max(1, Math.min(QUICK, Math.floor(ticks / semitones.length)));
  const strokes: Stroke[] = [];
  for (let at = 0; at < semitones.length - 1; at += 1) {
    strokes.push({ semitones: semitones[at] ?? 0, start: at * quick, ticks: quick });
  }
  const start = (semitones.length - 1) * quick;
  strokes.push({
    semitones: semitones[semitones.length - 1] ?? 0,
    start,
    ticks: Math.max(1, ticks - start),
  });
  return strokes;
}

/** A trill: the written note and the step above it, as fast as the note allows. */
function trilled(up: number, ticks: number): Stroke[] {
  return evenly([0, up], Math.max(2, Math.round(ticks / QUICK)), ticks);
}

/**
 * A turn taken late: the note is held, and the turn happens as it ends.
 *
 * This is the one ornament whose sign is written between two notes rather than over one,
 * and the delay is the whole of what distinguishes it, so the note keeps half its length
 * before the four notes of the turn begin.
 */
function delayed(turn: readonly number[], ticks: number): Stroke[] {
  const held = Math.floor(ticks / 2);
  const rest = bitten(turn, ticks - held);
  return [
    { semitones: 0, start: 0, ticks: held },
    ...rest.map((stroke) => ({ ...stroke, start: stroke.start + held })),
  ];
}

/** The notes an ornament asks for, or `null` for a sign this does not expand. */
function ornamented(
  event: NoteEvent,
  pitch: NotePitch,
  fifths: number,
  ticks: number,
): Stroke[] | null {
  const mark = event.ornaments[0];
  if (mark === undefined || ticks < 2) return null;
  const up = degree(pitch, fifths, 1);
  const down = degree(pitch, fifths, -1);

  switch (mark) {
    case "trill":
    case "shake":
      return trilled(up, ticks);
    case "mordent":
      return bitten([0, down, 0], ticks);
    case "inverted-mordent":
      return bitten([0, up, 0], ticks);
    case "turn":
      return bitten([up, 0, down, 0], ticks);
    case "inverted-turn":
      return bitten([down, 0, up, 0], ticks);
    case "delayed-turn":
      return delayed([up, 0, down, 0], ticks);
    case "schleifer":
      return bitten([degree(pitch, fifths, -2), down, 0], ticks);
  }
}

/**
 * A tremolo: the note struck over and over at the speed its beams ask for.
 *
 * One beam through the stem means quavers, two means semiquavers, three means demisemis --
 * the same reading as a beam over a note, which is what the stroke is drawn as.
 */
function repeated(beams: number, ticks: number): Stroke[] | null {
  const unit = PPQ / 2 ** Math.min(beams, 3);
  const count = Math.max(2, Math.round(ticks / unit));
  return ticks < 2 ? null : evenly([0], count, ticks);
}

/**
 * How a written note is actually sounded, or `null` when it is sounded as written.
 *
 * A drum is struck rather than played, so only the tremolo -- which on a drum is a roll --
 * reaches it; a trill on a snare means nothing and is left alone.
 */
export function strokesFor(
  event: NoteEvent,
  pitch: NotePitch,
  fifths: number,
  ticks: number,
): readonly Stroke[] | null {
  if (event.tremolo > 0) return repeated(event.tremolo, ticks);
  if (pitch.drum !== null) return null;
  return ornamented(event, pitch, fifths, ticks);
}

/**
 * How long after the chord each of its notes is struck, when it is spread rather than
 * struck together.
 *
 * The answer is indexed as the chord's pitches are, and is empty for a chord that is played
 * as one -- which includes the bracket, the sign that exists to say "do not spread this".
 * The order is by sound and not by the order the pitches happen to be stored in, because a
 * chord is spread from the bottom up whatever order it was typed in.
 */
export function spreadOf(event: NoteEvent, ticks: number): readonly number[] {
  const count = event.pitches.length;
  const upwards = event.arpeggio === "up";
  if ((!upwards && event.arpeggio !== "down") || count < 2) return [];
  if (event.pitches.some((pitch) => pitch.drum !== null)) return [];

  const each = Math.min(PPQ / 8, Math.floor(ticks / (2 * count)));
  if (each < 1) return [];

  const order = event.pitches
    .map((pitch, at) => ({ at, midi: midiOf(pitch) }))
    .sort((one, other) => one.midi - other.midi)
    .map((entry) => entry.at);

  const delays = new Array<number>(count).fill(0);
  order.forEach((at, rank) => {
    delays[at] = (upwards ? rank : count - 1 - rank) * each;
  });
  return delays;
}
